import { Capacitor } from "@capacitor/core";
import { FirebaseAppCheck } from "@capacitor-firebase/app-check";
import {
  initializeAppCheck,
  CustomProvider,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import posthog from "posthog-js";
import { app } from "./firebase";
import { getCurrentVersion } from "native/version";

/**
 * Initialize Firebase App Check.
 * - Web: Uses reCAPTCHA Enterprise via Firebase JS SDK
 * - iOS/Android: Uses a CustomProvider that bridges tokens from the native
 *   Capacitor App Check plugin (App Attest / Play Integrity) into the
 *   Firebase JS SDK so that Firestore/Storage calls from the webview
 *   include valid App Check tokens.
 */
export async function initializeAppCheckForPlatform(): Promise<void> {
  const platform = Capacitor.getPlatform();
  const appVersion = getCurrentVersion();
  // Stamp every PostHog event with platform + app version so the ~4% of App
  // Check requests Firebase reports as invalid/unknown-origin can be correlated
  // to a client (chiefly old app versions) before any enforcement is flipped on.
  posthog.register({ platform, appVersion });

  try {
    if (Capacitor.isNativePlatform()) {
      // Initialize the native App Check SDK (App Attest on iOS, Play Integrity on Android)
      // Disable native auto-refresh — the JS SDK's CustomProvider handles token lifecycle
      await FirebaseAppCheck.initialize({
        isTokenAutoRefreshEnabled: false,
      });

      // Cache the token so we don't hit the native bridge on every Firestore call
      let cachedToken: { token: string; expireTimeMillis: number } | null =
        null;

      // Bridge native tokens into the Firebase JS SDK via a CustomProvider
      const customProvider = new CustomProvider({
        getToken: async () => {
          // Return cached token if it's still valid (with 5 min buffer)
          if (
            cachedToken &&
            cachedToken.expireTimeMillis > Date.now() + 300000
          ) {
            return cachedToken;
          }
          let result;
          try {
            result = await FirebaseAppCheck.getToken({ forceRefresh: false });
          } catch (e) {
            // Native attestation (Play Integrity / App Attest) failed — this is
            // the request that shows up as "invalid" in Firebase. Log it so we
            // can see which platforms/versions can't produce a token.
            posthog.capture("app_check_failed", {
              stage: "native_getToken",
              platform,
              appVersion,
              error: e instanceof Error ? e.message : String(e),
            });
            throw e;
          }
          cachedToken = {
            token: result.token,
            expireTimeMillis: result.expireTimeMillis ?? Date.now() + 3600000,
          };
          return cachedToken;
        },
      });

      initializeAppCheck(app, {
        provider: customProvider,
        isTokenAutoRefreshEnabled: true,
      });

      console.log(`App Check initialized for ${Capacitor.getPlatform()}`);
    } else {
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        console.warn(
          "App Check: REACT_APP_RECAPTCHA_SITE_KEY not set, skipping web App Check"
        );
        return;
      }

      // Enable debug provider on localhost. Use a FIXED token from the env so it's
      // registered once (Firebase Console > App Check > Apps > Manage debug tokens)
      // and reused across browsers/profiles; fall back to `true` (random token
      // logged to console, must be registered each time) when the env isn't set.
      if (window.location.hostname === "localhost") {
        (
          window as unknown as Record<string, unknown>
        ).FIREBASE_APPCHECK_DEBUG_TOKEN =
          process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || true;
      }

      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });

      console.log("App Check initialized for web with reCAPTCHA Enterprise");
    }
  } catch (error) {
    console.error("Failed to initialize App Check:", error);
    posthog.capture("app_check_failed", {
      stage: "initialize",
      platform,
      appVersion,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
